import { defineStyle, defineStyleConfig } from '@chakra-ui/styled-system';

// Base button style: consistent typography, rounding, and transitions
const baseStyle = defineStyle({
  borderRadius: 'md',
  fontWeight: 600,
  _focusVisible: {
    boxShadow: '0 0 0 2px var(--chakra-colors-accent-default)',
  },
  _disabled: {
    opacity: 1,
    bg: 'bg.disabled',
    color: 'text.muted',
    borderColor: 'border.muted',
    cursor: 'not-allowed',
  },
});

// Button sizes with comfortable height for text and proper circular IconButtons
const sizes = {
  sm: {
    h: '40px', // Comfortable small button height
    minH: '40px',
    minW: '40px', // Ensure IconButtons are square for circular shape
    px: 4,
    fontSize: '14px',
    lineHeight: '1.5',
  },
  md: {
    h: '44px', // Comfortable default button height
    minH: '44px',
    minW: '44px', // Ensure IconButtons are square for circular shape
    px: 5,
    fontSize: '15px',
    lineHeight: '1.5',
  },
  lg: {
    h: '52px', // Large comfortable button height
    minH: '52px',
    minW: '52px', // Ensure IconButtons are square for circular shape
    px: 6,
    fontSize: '16px',
    lineHeight: '1.5',
  },
};

// Variants
const solid = defineStyle({
  bg: 'accent.default',
  color: 'accent.on',
  _hover: {
    filter: 'brightness(0.95)',
  },
  _active: {
    filter: 'brightness(0.9)',
  },
});

const outline = defineStyle({
  bg: 'transparent',
  color: 'text.primary',
  borderWidth: '1px',
  borderColor: 'border.muted',
  _hover: {
    bg: 'bg.subtle',
  },
  _active: {
    bg: 'bg.subtle.hover',
  },
});

// White variant: card-like surface used as default for consistency
const white = defineStyle({
  bg: 'bg.elevated',
  color: 'text.primary',
  borderWidth: '1px',
  borderColor: 'border.muted',
  boxShadow: 'elevation.1',
  _hover: {
    boxShadow: 'elevation.2',
  },
  _active: {
    filter: 'brightness(0.98)',
  },
});

const ghost = defineStyle({
  color: 'text.primary',
  _hover: {
    bg: 'bg.subtle',
  },
  _active: {
    bg: 'bg.subtle.hover',
  },
});

export const Button = defineStyleConfig({
  baseStyle,
  sizes,
  variants: { solid, outline, white, ghost },
  defaultProps: {
    variant: 'white',
    size: 'md',
  },
});
